import { useEffect, useMemo, useRef, useState } from 'react';
import { categories, topics, columns, updates } from '../data';

type SearchHit = {
  label: string;
  hint: string;
  target: string;
};

const navLinks = [
  { label: '今日热榜', href: '#hot' },
  { label: '分类导航', href: '#category' },
  { label: '精品专题', href: '#topic' },
  { label: '常设栏目', href: '#column' },
  { label: '标签云', href: '#tags' },
];

function buildPool(): SearchHit[] {
  const pool: SearchHit[] = [];
  categories.forEach((c) => {
    pool.push({ label: c.name, hint: c.desc, target: `cat-${c.id}` });
    c.tags.forEach((t) => pool.push({ label: t, hint: `${c.name} · 标签`, target: `cat-${c.id}` }));
  });
  topics.forEach((t) => pool.push({ label: t.name, hint: t.kicker, target: `topic-card-${t.id}` }));
  columns.forEach((c) => pool.push({ label: c.name, hint: c.host, target: `column-card-${c.id}` }));
  updates.forEach((u) => pool.push({ label: u.title, hint: u.cat, target: 'update' }));
  return pool;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showPanel, setShowPanel] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const pool = useMemo(buildPool, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setShowPanel(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return pool.filter((i) => i.label.toLowerCase().includes(q)).slice(0, 8);
  }, [query, pool]);

  const go = (target: string) => {
    setOpen(false);
    setShowPanel(false);
    setQuery('');
    const el = document.getElementById(target);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const inputClass =
    'w-full rounded-full bg-background-100/90 text-sm text-foreground-800 outline-none ring-1 ring-transparent transition placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-400 px-4 pl-11 pr-10 py-2.5';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/90 shadow-soft backdrop-blur-md' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 md:h-[68px] w-full max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        <a href="#home" className="flex items-center gap-2 whitespace-nowrap" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-emotion-happy-line text-lg" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-display text-xl text-primary-700">导航吃瓜</span>
            <span className="text-[10px] tracking-wide text-foreground-400">分类 · 专题 · 栏目索引</span>
          </span>
        </a>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="rounded-full px-4 py-2 text-sm font-medium text-foreground-600 transition hover:bg-background-200/70 hover:text-foreground-950"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            className="ml-2 inline-flex whitespace-nowrap items-center gap-1.5 rounded-full bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-accent-600"
          >
            <i className="ri-add-line" aria-hidden="true"></i>建议收录
          </a>
        </nav>

        <div className="flex items-center gap-2">
          <div ref={boxRef} className="relative hidden w-60 md:block">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
              <i className="ri-search-line" aria-hidden="true"></i>
            </span>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowPanel(true);
              }}
              onFocus={() => setShowPanel(true)}
              className={inputClass}
              placeholder="搜索分类 / 专题 / 栏目…"
              type="search"
            />
            {matches.length > 0 ? (
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-foreground-400 hover:text-primary-600"
                onClick={() => {
                  setShowPanel(false);
                  setQuery('');
                }}
              >
                <i className="ri-close-line" aria-hidden="true"></i>
              </span>
            ) : null}
            {showPanel && matches.length > 0 ? (
              <div className="absolute inset-x-0 top-12 overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-soft">
                <ul>
                  {matches.map((m) => (
                    <li key={m.label + m.hint}>
                      <button
                        type="button"
                        onClick={() => go(m.target)}
                        className="flex w-full flex-col items-start gap-0.5 px-4 py-3 text-left transition hover:bg-background-100"
                      >
                        <span className="text-sm font-medium text-foreground-900">{m.label}</span>
                        <span className="text-xs text-foreground-400">{m.hint}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          <button
            className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-700 hover:bg-background-200/70 lg:hidden"
            aria-label="打开菜单"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <i className={open ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {open ? (
        <nav
          className="border-t border-background-200 bg-background-50 px-4 pb-6 pt-3 lg:hidden"
          aria-label="移动端导航"
        >
          <div className="relative mb-3">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
              <i className="ri-search-line" aria-hidden="true"></i>
            </span>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowPanel(true);
              }}
              className="w-full rounded-full bg-background-100 px-4 py-2.5 pl-11 pr-4 text-sm outline-none ring-1 ring-background-200 focus:ring-2 focus:ring-primary-400"
              placeholder="搜索分类 / 专题 / 栏目…"
              type="search"
            />
          </div>
          <ul className="flex flex-col">
            {navLinks.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between rounded-xl px-3 py-3 text-base font-medium text-foreground-700 hover:bg-background-100"
                >
                  {l.label}
                  <i className="ri-arrow-right-s-line text-foreground-300" aria-hidden="true"></i>
                </a>
              </li>
            ))}
          </ul>
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-3 text-sm font-semibold text-background-50"
          >
            <i className="ri-add-line" aria-hidden="true"></i>建议收录
          </a>
          {query.trim() && matches.length > 0 ? (
            <div className="mt-3 overflow-hidden rounded-2xl border border-background-200">
              <ul>
                {matches.slice(0, 5).map((m) => (
                  <li key={m.label + m.hint}>
                    <button
                      type="button"
                      onClick={() => go(m.target)}
                      className="flex w-full flex-col items-start gap-0.5 bg-background-100/60 px-3 py-2.5 text-left hover:bg-background-200/70"
                    >
                      <span className="text-sm text-foreground-900">{m.label}</span>
                      <span className="text-xs text-foreground-400">{m.hint}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </nav>
      ) : null}
    </header>
  );
}